import type { MantineColorSchemeManager } from '@mantine/core';

const VALID_COLOR_SCHEMES = ['light', 'dark', 'auto'] as const;

function isMantineColorScheme(value: unknown): value is 'light' | 'dark' | 'auto' {
  return typeof value === 'string' && VALID_COLOR_SCHEMES.includes(value as (typeof VALID_COLOR_SCHEMES)[number]);
}

/**
 * Options for the safe localStorage color scheme manager.
 */
export interface SafeColorSchemeManagerOptions {
  /** Local storage key. Must match Mantine's default for existing stored values to work. */
  key?: string;
  /**
   * When this returns true, set() will not write to localStorage.
   * Used so menu pages can set theme (e.g. dark) without persisting it,
   * preventing cross-tab flicker and wrong initial theme on non-menu routes.
   */
  getSkipPersist?: () => boolean;
}

/**
 * Creates a Mantine color scheme manager that uses localStorage but never throws
 * when storage is full (quota exceeded) or unavailable (e.g. private browsing).
 *
 * When localStorage.setItem fails (e.g. QuotaExceededError), the failure is
 * swallowed so the app continues to work; the theme just won't persist until
 * the user frees space or reloads.
 *
 * Use this as MantineProvider's colorSchemeManager to avoid:
 *   "DOMException: The quota has been exceeded"
 */
export function safeLocalStorageColorSchemeManager(
  options: SafeColorSchemeManagerOptions = {}
): MantineColorSchemeManager {
  const key = options.key ?? 'mantine-color-scheme-value';
  const getSkipPersist = options.getSkipPersist;
  let handleStorageEvent: (event: StorageEvent) => void;

  return {
    get: (defaultValue) => {
      if (typeof window === 'undefined') return defaultValue;
      try {
        const stored = window.localStorage.getItem(key);
        return isMantineColorScheme(stored) ? stored : defaultValue;
      } catch {
        return defaultValue;
      }
    },

    set: (value) => {
      if (typeof window === 'undefined') return;
      // When menu pages set theme, skip persisting so other tabs don't flicker
      if (getSkipPersist?.()) return;
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // Swallow QuotaExceededError and other storage errors so the app
        // keeps working; theme simply won't persist until storage has space.
      }
    },

    subscribe: (onUpdate) => {
      handleStorageEvent = (event: StorageEvent) => {
        if (event.storageArea === window.localStorage && event.key === key && isMantineColorScheme(event.newValue)) {
          onUpdate(event.newValue);
        }
      };
      window.addEventListener('storage', handleStorageEvent);
    },

    unsubscribe: () => {
      window.removeEventListener('storage', handleStorageEvent);
    },

    clear: () => {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Ignore errors (e.g. quota, private mode).
      }
    },
  };
}
